/**
 * Shape styling utilities for applying visual effects to Konva shapes
 */

import Konva from 'konva';
import { ShadowEffect, StrokeStyle, BlurEffect, BlendMode } from '../editing/types';

/**
 * Apply shadow effects to a Konva shape node
 * @param node - Konva shape node
 * @param shadows - Array of shadow effects
 */
export function applyShadows(node: any, shadows: ShadowEffect[]): void {
  if (!node || !shadows || shadows.length === 0) {
    // Reset shadow properties
    node.shadowColor(undefined);
    node.shadowBlur(0);
    node.shadowOffset({ x: 0, y: 0 });
    node.shadowOpacity(0);
    return;
  }

  // Konva supports one shadow at a time, so we'll use the first one
  // For multiple shadows, we'd need to render multiple layers
  const shadow = shadows[0];
  
  node.shadowColor(shadow.color);
  node.shadowBlur(shadow.blur);
  node.shadowOffset({ x: shadow.offsetX, y: shadow.offsetY });
  node.shadowOpacity(shadow.opacity);
  
  // For inner shadows, we'd need custom rendering with Shape component
  // For now, we'll apply it as a regular shadow
}

/**
 * Apply stroke style to a Konva shape node
 * @param node - Konva shape node
 * @param strokeStyle - Stroke style configuration
 */
export function applyStrokeStyle(node: any, strokeStyle: StrokeStyle | undefined): void {
  if (!node || !strokeStyle) {
    return;
  }

  // Apply dash pattern
  switch (strokeStyle.pattern) {
    case 'solid':
      node.dash([]);
      break;
    case 'dashed':
      node.dash([10, 5]);
      break;
    case 'dotted':
      node.dash([2, 5]);
      break;
    case 'custom':
      if (strokeStyle.dashArray) {
        node.dash(strokeStyle.dashArray);
      }
      break;
  }

  // Apply stroke alignment (Konva doesn't directly support this, but we can simulate it)
  // For now, we'll note it in the data but actual implementation would require
  // custom rendering or adjusting stroke width
  if (strokeStyle.align) {
    // Store alignment info - actual rendering adjustment would be done in renderer
    node.setAttr('strokeAlign', strokeStyle.align);
  }
}

/**
 * Create default shadow effect
 */
export function createDefaultShadow(type: 'drop' | 'inner' = 'drop'): ShadowEffect {
  return {
    type,
    color: 'rgba(0, 0, 0, 0.2)',
    blur: 8,
    offsetX: 0,
    offsetY: 4,
    opacity: 0.2,
  };
}

/**
 * Create default stroke style
 */
export function createDefaultStrokeStyle(): StrokeStyle {
  return {
    pattern: 'solid',
    align: 'center',
  };
}

/**
 * Apply blur effect to a Konva shape node
 * @param node - Konva shape node
 * @param blur - Blur effect configuration
 */
export function applyBlurEffect(node: any, blur: BlurEffect | undefined): void {
  if (!node) return;
  
  if (!blur || blur.radius === 0) {
    // Reset blur
    node.filters([]);
    node.blurRadius(0);
    // Clear cache when removing blur
    if (node.cache) {
      node.clearCache();
    }
    return;
  }

  // For layer blur, use Konva's Blur filter
  if (blur.type === 'layer') {
    node.filters([Konva.Filters.Blur]);
    node.blurRadius(blur.radius);
    // Cache the node for better performance with filters
    node.cache();
  } else if (blur.type === 'background') {
    // Background blur requires more complex rendering
    // For now, we'll apply it as layer blur
    // Full background blur would require separate layer rendering
    node.filters([Konva.Filters.Blur]);
    node.blurRadius(blur.radius);
    node.cache();
  }
}

/**
 * Apply blend mode to a Konva shape node
 * @param node - Konva shape node
 * @param blendMode - Blend mode to apply
 */
export function applyBlendMode(node: any, blendMode: BlendMode | undefined): void {
  if (!node || !blendMode || blendMode === 'normal') {
    node.globalCompositeOperation('source-over');
    return;
  }

  // Map blend modes to Konva's globalCompositeOperation
  const blendModeMap: Record<BlendMode, string> = {
    'normal': 'source-over',
    'multiply': 'multiply',
    'screen': 'screen',
    'overlay': 'overlay',
    'darken': 'darken',
    'lighten': 'lighten',
    'color-dodge': 'color-dodge',
    'color-burn': 'color-burn',
    'hard-light': 'hard-light',
    'soft-light': 'soft-light',
    'difference': 'difference',
    'exclusion': 'exclusion',
  };

  const konvaMode = blendModeMap[blendMode] || 'source-over';
  node.globalCompositeOperation(konvaMode);
}

/**
 * Create default blur effect
 */
export function createDefaultBlur(type: 'layer' | 'background' = 'layer'): BlurEffect {
  return {
    type,
    radius: 10,
  };
}


